import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { buildPlates } from '../../../utils/emailPlates';

// Human-readable `title` attributes per plate status, so a hover explains *why*
// a plate looks the way it does instead of leaving the user to guess. This is
// also where the 'not-needed' vs 'missing' distinction is spelled out in words.
const titleFor = (status, language) => {
  const label = language.toUpperCase();
  switch (status) {
    case 'present':
      return `${label} plate is set — open it`;
    case 'missing':
      return `${label} plate is missing — this campaign should have one`;
    case 'inactive':
      return `${label} plate exists but is switched off — it is not sending. Open it to review and activate it`;
    case 'not-needed':
    default:
      return 'List-kind campaigns use the generic newsletter by design — no per-language email is expected here';
  }
};

// The design's `.generic` marker: a list-kind campaign rides the generic
// newsletter, so it has no plates at all rather than four empty ones.
const GenericMarker = () => (
  <span
    className="generic"
    title="List-kind campaigns use the generic newsletter by design — no per-language email is expected here"
  >
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M1.5 3.5h9v5h-9zM1.5 3.5L6 7l4.5-3.5" strokeLinejoin="round" />
    </svg>
    generic email
  </span>
);

// The plate strip, the one element unique to this tool: a row of small square
// cells, one per language, coloured by state — not a list of links. Cells with
// a plate are dark and clickable; a missing one is an outlined empty square.
const EmailPlateStrip = ({ campaign, languages }) => {
  if (campaign.kind === 'list') {
    return <GenericMarker />;
  }

  const plates = buildPlates(campaign, languages);

  return (
    <span className="plates">
      {plates.map(({ language, status }) => {
        const title = titleFor(status, language);

        if (status === 'not-needed') {
          return <GenericMarker key={language} />;
        }

        // 'inactive' is a plate that exists and is not sending. It links like a
        // set plate, because the whole point of showing it is that there is
        // something there to open — but it is drawn differently, because treating
        // it as set would claim this campaign covers a language it does not.
        if (status === 'present' || status === 'inactive') {
          return (
            <Link
              key={language}
              to={`/cms/emails/${campaign.key}/${language}`}
              className={status === 'present' ? 'plate-cell is-set' : 'plate-cell is-off'}
              title={title}
            >
              {language}
            </Link>
          );
        }

        return (
          <span key={language} className="plate-cell is-empty" title={title}>
            {language}
          </span>
        );
      })}
    </span>
  );
};

EmailPlateStrip.propTypes = {
  campaign: PropTypes.shape({
    key: PropTypes.string.isRequired,
    kind: PropTypes.string,
    languages: PropTypes.arrayOf(PropTypes.shape({
      language: PropTypes.string,
      status: PropTypes.string,
    })),
  }).isRequired,
  languages: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default EmailPlateStrip;
